import React, { useState, useEffect } from 'react';
import StringMask from 'string-mask';
import { Button } from 'components/commons';
import Loading from 'components/loading';
import LoanInfo from 'components/loan-info';
import Title from 'components/title';
import {
    useAvailableCouponByCodeLazyQuery,
    useContinueLoanApplicationWorkFlowMutation,
    useGetLoanQuery
} from 'generated/graphql';
import { useNavigation } from 'hooks/index';
import { nextStep } from 'actions/step.action';
import { calculateDiscounts, hasComissionDiscount, hasDiscountsToApply } from 'utils/discount-utils';
import { IDiscountCalculated } from 'interfaces/interfaces';
import { useProfile } from 'providers/profile.provider';
import { ESTIMULO_2020 } from 'constants/constants';
import { getLoanApplicationParams } from 'utils/utils';
import editIcon from 'assets/img/editar@3x.png';

const ConfirmationPage: React.FC = () => {
    const [continueWorkFlow] = useContinueLoanApplicationWorkFlowMutation();
    const cepFormatter = new StringMask("00000-000");
    const [discountCalculated, setDiscountCalculated] = useState<IDiscountCalculated>({});
    const [discounts, setDiscounts] = useState([]);
    const [isProcessing, setProcessing] = useState(false);
    const {
        goToLoanConfiguration, goToAddressConfiguration, goToBankAccountConfiguration,
        goToPreviousRoute, goToWelcome, goToError,
    } = useNavigation();
    const { loanApplicationUtmSource } = useProfile();
    const loanApplicationId = getLoanApplicationParams();
    const [availableCouponByCodeQuery] = useAvailableCouponByCodeLazyQuery();

    const { data, loading, refetch } = useGetLoanQuery({
        variables: { id: loanApplicationId },
        fetchPolicy:"network-only"
    });


    const loadDiscountsIfAvailable = async () => {
        if (data?.loanApplication.couponCode) {
            const response = await availableCouponByCodeQuery({
                variables: {
                    code: data?.loanApplication.couponCode
                }
            })

            if (response.data?.availableCouponByCode.discounts
                && hasDiscountsToApply(
                    response.data.availableCouponByCode.discounts, data?.loanApplication)) {
                setDiscounts(response.data.availableCouponByCode.discounts);
            }
        }
    }

    useEffect(() => {
        loadDiscountsIfAvailable();
        refetch();
    }, [data?.loanApplication]);

    useEffect(() => {
        setDiscountCalculated(
            calculateDiscounts(discounts, data?.loanApplication)
        );
    }, [discounts]);

    if (loading || isProcessing) {
        return (
            <Loading />
        );
    }

    const {
        borrower: {
            address, bankAccount
        },
    } = data?.loanApplication;

    const isSubsequentEstimuloLoan =
        data?.loanApplication.renewalInformation.previousLoansCount > 0
        && loanApplicationUtmSource === ESTIMULO_2020;
    const loanCountInWords = ['primeira', 'segunda', 'terceira'][data?.loanApplication.renewalInformation.previousLoansCount];

    const acceptProposal = async (loanApplicationId: string) => {
        try {
            const nextScreen = goToWelcome;
            setProcessing(true);
            await continueWorkFlow({ variables: { loanApplicationId } });

            nextStep();
            nextScreen();
        } catch (error) {
            goToError();
        }
    }

    return (
        <>
            <div className={address.zipCode && bankAccount.account ? "confirmation-page" : (address.zipCode && !bankAccount.account ? "confirmation-page-address" : "confirmation-page-bank")}>
                <Title>
                    {
                        isSubsequentEstimuloLoan
                            ? `aqui está sua ${loanCountInWords} proposta pré-aprovada`
                            : "tudo certo até aqui?"
                    }
                </Title>

                {isSubsequentEstimuloLoan &&
                    <p className="grid-sub-title pb-4">
                        As condições são exatamente iguais ao seu empréstimo anterior. Deseja continuar?
                    </p>
                }

                <LoanInfo
                    loanApplication={{
                        ...data?.loanApplication,
                        acceptedProposal: {
                            ...data?.loanApplication.acceptedProposal,
                            fixedComission: hasComissionDiscount(discounts, data?.loanApplication) ? true : data?.loanApplication?.acceptedProposal?.fixedComission,
                            interestRate: discountCalculated.calculatedInterestRate ? discountCalculated.calculatedInterestRate : data?.loanApplication?.acceptedProposal?.interestRate,
                            paymentDay: discountCalculated.calculatedPaymentDay ? discountCalculated.calculatedPaymentDay : data?.loanApplication?.acceptedProposal?.paymentDay
                        }
                    }}
                    discounts={discounts.filter(d => d.__typename === "ComissionDiscount")}
                    className={`${!isSubsequentEstimuloLoan && "clickable"}`}
                    onClick={() => {
                        if (!isSubsequentEstimuloLoan) {
                            goToLoanConfiguration();
                        }
                    }}
                    canEdit={!isSubsequentEstimuloLoan}
                />
                {address.zipCode && (
                    <div
                        style={!bankAccount.account ? { margin: '1.5rem 0rem !important' } : null}
                        className={`grid-address-summary highlight-card ${!isSubsequentEstimuloLoan && "clickable"}`}
                        onClick={() => {
                            if (!isSubsequentEstimuloLoan) {
                                goToAddressConfiguration();
                            }
                        }}
                    >
                        <h3 className="mb-4 d-inline-block">
                            endereço da empresa
                        </h3>
                        {!isSubsequentEstimuloLoan &&
                            <div className="d-inline-block float-right primary-text edit-button">
                                <a className="mr-2 font-weight-bold">
                                    editar
                                </a>
                                <img src={editIcon} className="edit-icon" />
                            </div>
                        }
                        <p className="mb-0">
                            {`${address.street}${address.number ? `, ${address.number}` : ""}${address.complement ? `. ${address.complement}` : ""}`}
                        </p>
                        <p className="mb-0">
                            {`${address.neighbourhood}, ${address.city.name} - ${address.city.state.abbreviation}`}
                        </p>
                        <p className="mb-0">
                            {cepFormatter.apply(address.zipCode)}
                        </p>
                    </div>
                )}
                {bankAccount.account && (
                    <div
                        style={!address.zipCode ? { margin: '1.5rem 0rem !important' } : null}
                        className={`grid-bankaccount-summary highlight-card ${!isSubsequentEstimuloLoan && "clickable"}`}
                        onClick={() => {
                            if (!isSubsequentEstimuloLoan) {
                                goToBankAccountConfiguration();
                            }
                        }}
                    >
                        <h3 className="mb-4 d-inline-block">
                            conta bancária da empresa
                        </h3>
                        {!isSubsequentEstimuloLoan &&
                            <div className="d-inline-block float-right primary-text edit-button">
                                <a className="mr-2 font-weight-bold">
                                    editar
                                </a>
                                <img src={editIcon} className="edit-icon" />
                            </div>
                        }
                        <p className="mb-0">
                            {bankAccount.bank.code} - {bankAccount.bank.name}
                        </p>
                        <p className="mb-0">
                            Agência {bankAccount.agency}
                        </p>
                        <p className="mb-0">
                            Conta Corrente: {bankAccount.account}
                        </p>
                    </div>
                )}
                {!isSubsequentEstimuloLoan &&
                    <Button
                        label="voltar"
                        variant="secondary"
                        className="button-return grid-button-return"
                        type="button"
                        onClick={goToPreviousRoute}
                    />
                }
                <p className="grid-confirmation-text px-3">
                    Esta será a proposta <br className="d-block d-xs-none" />analisada pela equipe da biz
                </p>
                <Button
                    label={`${isSubsequentEstimuloLoan ? "aceitar" : "continuar"}`}
                    className="grid-button-forward full-width"
                    onClick={() => acceptProposal(data?.loanApplication.id)}
                />
            </div>
            <div className="d-none d-sm-block mb-4" />
        </>
    );
}
export default ConfirmationPage;
